import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  StorageSettingsContext,
  type StorageSettingsContextValue,
  type StorageSettingsMap,
} from "@/contexts/use-storage-settings";

/**
 * Fetches public.storage_settings once per provider mount and exposes a
 * synchronous map to authenticated dashboard consumers.
 */
export function StorageSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<StorageSettingsMap>({});
  const [ready, setReady] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc("get_storage_settings");
      if (error) {
        console.error("[StorageSettings] get_storage_settings error:", error);
        setReady(true);
        return;
      }
      if (data && typeof data === "object" && !Array.isArray(data)) {
        const normalized: StorageSettingsMap = {};
        for (const [key, value] of Object.entries(data)) {
          normalized[key] = typeof value === "string" ? value : String(value);
        }
        setSettings(normalized);
      }
    } catch (error) {
      console.error("[StorageSettings] fetch failed:", error);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const value = useMemo<StorageSettingsContextValue>(
    () => ({
      settings,
      ready,
      get: (key, fallback = "") => settings[key] ?? fallback,
      setLocal: (key, value) => setSettings((previous) => ({ ...previous, [key]: value })),
      refresh: fetchSettings,
    }),
    [settings, ready, fetchSettings],
  );

  return <StorageSettingsContext.Provider value={value}>{children}</StorageSettingsContext.Provider>;
}
